/* global axios */
import { DataManager } from '../helper/CacheHelper/DataManager';
import ApiClient from './ApiClient';

class CacheEnabledApiClient extends ApiClient {
  constructor(resource, options = {}) {
    super(resource, options);
    this.dataManager = new DataManager(this.accountIdFromRoute);
  }

  // eslint-disable-next-line class-methods-use-this
  get cacheModelName() {
    throw new Error('cacheModelName is not defined');
  }

  get(cache = false) {
    if (cache) {
      return this.getFromCache();
    }

    return this.getFromNetwork();
  }

  getFromNetwork() {
    return axios.get(this.url);
  }

  // eslint-disable-next-line class-methods-use-this
  extractDataFromResponse(response) {
    return response.data.payload;
  }

  // eslint-disable-next-line class-methods-use-this
  marshallData(dataToParse) {
    return { data: { payload: dataToParse } };
  }

  async getFromCache() {
    try {
      // IDB is not supported in Firefox private mode: https://bugzilla.mozilla.org/show_bug.cgi?id=781982
      await this.dataManager.initDb();
    } catch {
      return this.getFromNetwork();
    }

    let cacheKeyFromApi;
    try {
      const { data } = await axios.get(
        `/api/v1/accounts/${this.accountIdFromRoute}/cache_keys`
      );
      cacheKeyFromApi = data.cache_keys[this.cacheModelName];
    } catch {
      return this.getFromNetwork();
    }

    let localData = [];
    try {
      const isCacheValid = await this.validateCacheKey(cacheKeyFromApi);
      if (isCacheValid) {
        localData = await this.dataManager.get({
          modelName: this.cacheModelName,
        });
      }
    } catch {
      // A broken local cache must never leave the store empty: refetch below.
      localData = [];
    }

    if (localData.length === 0) {
      return this.refetchAndCommit(cacheKeyFromApi);
    }

    return this.marshallData(localData);
  }

  async refetchAndCommit(newKey = null) {
    const response = await this.getFromNetwork();
    // Persisting is best effort and must not delay the fresh data.
    this.commitToCache(this.extractDataFromResponse(response), newKey);
    return response;
  }

  async commitToCache(data, newKey) {
    try {
      await this.dataManager.initDb();
      await this.dataManager.replace({ modelName: this.cacheModelName, data });
      await this.dataManager.setCacheKeys({ [this.cacheModelName]: newKey });
    } catch {
      // Ignore error
    }
  }

  async validateCacheKey(cacheKeyFromApi) {
    try {
      await this.dataManager.initDb();
      const cachekey = await this.dataManager.getCacheKey(this.cacheModelName);
      return cacheKeyFromApi === cachekey;
    } catch {
      // Unreadable cache counts as stale so callers refetch from the network.
      return false;
    }
  }
}

export default CacheEnabledApiClient;
